/**
 * Page Transition Utilities
 *
 * Utility functions for working with page transitions in multi-page designs.
 */

import {
  PAGE_TRANSITION_DIRECTIONAL_TYPES,
  PAGE_TRANSITION_MIN_DURATION,
  PAGE_TRANSITION_MAX_DURATION,
  PAGE_TRANSITION_DEFAULT_DURATION,
  PAGE_TRANSITION_DEFAULT_TYPE,
  PAGE_TRANSITION_DEFAULT_DIRECTION
} from "./pageTransitionConstants";

/**
 * Check if a transition type requires a direction parameter
 * @param {string} type - Transition type
 * @returns {boolean} True if the transition type needs a direction
 */
export const isDirectionalTransition = type => {
  return PAGE_TRANSITION_DIRECTIONAL_TYPES.includes(type);
};

/**
 * Validate and clamp transition duration to valid range
 * @param {number} duration - Duration in milliseconds
 * @returns {number} Clamped duration value
 */
export const validateTransitionDuration = duration => {
  if (typeof duration !== "number" || isNaN(duration)) {
    return PAGE_TRANSITION_DEFAULT_DURATION;
  }
  return Math.max(
    PAGE_TRANSITION_MIN_DURATION,
    Math.min(PAGE_TRANSITION_MAX_DURATION, Math.round(duration))
  );
};

/**
 * Get a default transition object
 * @returns {Object} Default transition configuration
 */
export const getDefaultTransition = () => ({
  type: PAGE_TRANSITION_DEFAULT_TYPE,
  duration: PAGE_TRANSITION_DEFAULT_DURATION
});

/**
 * Create a new transition object with optional direction
 * @param {string} type - Transition type
 * @param {number} duration - Duration in milliseconds
 * @param {string} [direction] - Direction for directional transitions
 * @returns {Object} Transition configuration
 */
export const createTransition = (
  type,
  duration = PAGE_TRANSITION_DEFAULT_DURATION,
  direction
) => {
  const transition = {
    type,
    duration: validateTransitionDuration(duration)
  };

  if (isDirectionalTransition(type)) {
    transition.direction = direction || PAGE_TRANSITION_DEFAULT_DIRECTION;
  }

  return transition;
};

/**
 * Calculate total timeline duration including all page durations and transitions
 * @param {Object} pages - Pages object keyed by pageId
 * @param {string[]} pagesOrder - Array of page IDs in order
 * @returns {number} Total duration in milliseconds
 */
export const calculateTotalDurationWithTransitions = (pages, pagesOrder) => {
  if (!pages || !pagesOrder || pagesOrder.length === 0) {
    return 0;
  }

  let totalDuration = 0;

  pagesOrder.forEach((pageId, index) => {
    const page = pages[pageId];
    if (!page) return;

    // Add page duration
    totalDuration += page.duration || 0;

    // Add outgoing transition duration (not for last page)
    if (index < pagesOrder.length - 1 && page.outgoingTransition) {
      const transition = page.outgoingTransition;
      if (transition.type && transition.type !== "none") {
        totalDuration +=
          transition.duration || PAGE_TRANSITION_DEFAULT_DURATION;
      }
    }
  });

  return totalDuration;
};

/**
 * Validate a transition object
 * @param {Object} transition - Transition to validate
 * @returns {boolean} True if transition is valid
 */
export const isValidTransition = transition => {
  if (!transition || typeof transition !== "object") {
    return false;
  }

  if (!transition.type || typeof transition.type !== "string") {
    return false;
  }

  if (
    transition.duration !== undefined &&
    (typeof transition.duration !== "number" || isNaN(transition.duration))
  ) {
    return false;
  }

  return true;
};

/**
 * Normalize a transition object to ensure it has all required properties
 * @param {Object} transition - Transition to normalize
 * @returns {Object} Normalized transition
 */
export const normalizeTransition = transition => {
  if (!isValidTransition(transition)) {
    return getDefaultTransition();
  }

  const normalized = {
    type: transition.type,
    duration: validateTransitionDuration(
      transition.duration ?? PAGE_TRANSITION_DEFAULT_DURATION
    )
  };

  if (isDirectionalTransition(transition.type)) {
    normalized.direction =
      transition.direction || PAGE_TRANSITION_DEFAULT_DIRECTION;
  }

  return normalized;
};

/**
 * Get the playback segment (page or transition) at a given timeline time
 * @param {Object} pages - Pages object keyed by pageId
 * @param {string[]} pagesOrder - Array of page IDs in order
 * @param {number} currentTimeMs - Current playback time in milliseconds
 * @returns {Object} Segment info: { type: 'page' | 'transition', pageId, nextPageId?, transition?, progress? }
 */
export const getPlaybackSegmentAtTime = (pages, pagesOrder, currentTimeMs) => {
  if (!pages || !pagesOrder || pagesOrder.length === 0) {
    return { type: "page", pageId: null };
  }

  // Clamp to non-negative
  const time = Math.max(0, currentTimeMs);

  let accumulatedTime = 0;

  for (let i = 0; i < pagesOrder.length; i++) {
    const pageId = pagesOrder[i];
    const page = pages[pageId];
    if (!page) continue;

    const pageDuration = page.duration || 0;
    const pageEndTime = accumulatedTime + pageDuration;

    // Check if we're within this page's content
    if (time < pageEndTime) {
      return {
        type: "page",
        pageId,
        pageIndex: i,
        pageStartTime: accumulatedTime,
        pageEndTime,
        timeInPage: time - accumulatedTime
      };
    }

    accumulatedTime = pageEndTime;

    // Check if there's a transition after this page (not for last page)
    if (i < pagesOrder.length - 1 && page.outgoingTransition) {
      const transition = page.outgoingTransition;
      if (transition.type && transition.type !== "none") {
        const transitionDuration =
          transition.duration || PAGE_TRANSITION_DEFAULT_DURATION;
        const transitionEndTime = accumulatedTime + transitionDuration;

        // Check if we're within this transition
        if (time < transitionEndTime) {
          const nextPageId = pagesOrder[i + 1];
          const timeInTransition = time - accumulatedTime;
          const progress = timeInTransition / transitionDuration;

          return {
            type: "transition",
            pageId,
            nextPageId,
            pageIndex: i,
            transition,
            transitionStartTime: accumulatedTime,
            transitionEndTime,
            timeInTransition,
            progress // 0 to 1
          };
        }

        accumulatedTime = transitionEndTime;
      }
    }
  }

  // Past the end - return last page
  const lastPageId = pagesOrder[pagesOrder.length - 1];
  return {
    type: "page",
    pageId: lastPageId,
    pageIndex: pagesOrder.length - 1,
    isComplete: true
  };
};
